import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError } from "@refinedev/core";
import Button from "components/button";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal";
import Select from "components/select";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

interface ShowTimeModalProps extends IModalProps {
  defaultValue?: string;
  onSubmit: (data: string) => void;
}

interface IShowTimeForm {
  hour: string;
  minute: string;
}

const ShowTimeSchema = () => {
  return z.object({
    hour: z.string().min(1, "This field is required"),
    minute: z.string().min(1, "This field is required")
  }); 
}

export const ShowTimeModal = ({
  open,
  onClose,
  defaultValue,
  onSubmit,
}: ShowTimeModalProps) => {
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<IShowTimeForm, HttpError, IShowTimeForm>({
    defaultValues: {
      hour: !isEmpty(defaultValue) 
      ? defaultValue 
      : ""
    },
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        ShowTimeSchema()
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        hour: !isEmpty(defaultValue) ? defaultValue?.split(":")[0] ?? "" : "",
        minute: !isEmpty(defaultValue) ? defaultValue?.split(":")[1] ?? "" : ""
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [open]);

  const _submit = (values: IShowTimeForm) => {
    onSubmit(`${values.hour}:${values.minute}`);
    onClose();
  };

  const hoursArr = [...Array(24).keys()].map((index: number) => (
    { value: ('0' + (index)).slice(-2), label: ('0' + (index)).slice(-2) }
  ));
  const minuteArr = [...Array(60).keys()].map((index: number) => (
    { value: ('0' + (index)).slice(-2), label: ('0' + (index)).slice(-2) }
  ));

  return (
    <Modal title={`${!isEmpty(defaultValue) ? "Edit" : "Add"} showtime`} onClose={onClose} open={open}>
      <div>
        <div className="w-full divide-y divide-gray-100 text-sm">
          <div className="box-container">
            <div className="flex items-start gap-x-3">
              <div className="flex-1">
                <Label
                  required
                  id="hour"
                  name="Hour"
                />
                <Controller
                  control={control}
                  name="hour"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="hour"
                        inputRef={ref}
                        options={hoursArr}
                        id="hour"
                        placeholder="Hour"
                        defaultValue={
                          hoursArr.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={hoursArr.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                        }}
                        error={get(errors, `hour`, "")}
                      />
                    );
                  }}
                />
                {get(errors, "hour") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `hour.message`)}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="flex-1">
                <Label
                  required
                  id="minute"
                  name="Minute"
                />
                <Controller
                  control={control}
                  name="minute"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="minute"
                        inputRef={ref}
                        options={minuteArr}
                        id="minute"
                        placeholder="Minute"
                        defaultValue={
                          minuteArr.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={minuteArr.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                        }}
                        error={get(errors, `minute`, "")}
                      />
                    );
                  }}
                />
                {get(errors, "minute") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `minute.message`)}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          <div className="w-full box-container py-4">
            <div className="flex items-center justify-end gap-x-3">
              <Button
                className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                onClick={onClose}
              >
                CANCEL
              </Button>
              <Button className="w-20" onClick={handleSubmit(_submit)}>
                {!isEmpty(defaultValue) ? "UPDATE" : "ADD"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
};
